import React, { createContext, useEffect, useRef, useState } from 'react';

import * as apiLogin from 'api/user/PostLoginV1';
import * as apiLogout from 'api/user/PostLogoutV1';
import * as apiRefreshToken from 'api/user/PostRefreshTokenV1';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import PERMISSIONS from 'enums/permissions';
import ITokenJWT from 'interfaces/ITokenJWT';
import ITokens from 'interfaces/ITokens';

interface JwtTokenContextType {
	setTokens: (newTokens: ITokens | null) => void; // only for OIDC login
	accessJwt: ITokenJWT | null;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	arePermissionsInToken: (permissions: PERMISSIONS[]) => boolean;
}

export const JwtContext = createContext<JwtTokenContextType | undefined>(undefined);

function parseJwt(token: string): ITokenJWT {
	var base64Url = token.split('.')[1];
	var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	var jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(function (c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join('')
	);
	return JSON.parse(jsonPayload);
}

export const TOKENS_LOCAL_STORAGE_KEY = 'tokens';

export const JwtProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [tokens, setTokensState] = useState<ITokens | null>(null);
	const [accessJwtPayload, setAccessJwtPayload] = useState<ITokenJWT | null>(null);
	const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const onlineHandlerRef = useRef<(() => void) | null>(null);
	const channelRef = useRef<BroadcastChannel | null>(null);

	useEffect(() => {
		channelRef.current = new BroadcastChannel('broadcast_set_token');
		channelRef.current.onmessage = (event) => {
			setTokensState(event.data);
		};

		return () => {
			channelRef.current?.close();
		};
	}, []);

	useEffect(() => {
		const storedTokens = localStorage.getItem(TOKENS_LOCAL_STORAGE_KEY);
		if (!storedTokens) return;

		const parsedTokens: ITokens = JSON.parse(storedTokens);
		setTokens(parsedTokens);
	}, []);

	useEffect(() => {
		if (!accessJwtPayload?.exp) return;

		const scheduleRefresh = () => {
			const now = Date.now();
			const refreshOffset = 10 * 1000;
			const expirationTime = accessJwtPayload.exp * 1000 - refreshOffset;

			clearRefreshTimeout();
			if (expirationTime <= now) {
				refreshTokens();
			} else {
				refreshTimeoutRef.current = setTimeout(refreshTokens, expirationTime - now);
			}
		};

		// Refresh token only in the focused tab, so we avoid race conditions of 2 or more refresh endpoint calls
		if (document.hasFocus()) scheduleRefresh();

		const handleBlur = clearRefreshTimeout;
		const handleFocus = scheduleRefresh;
		window.addEventListener('blur', handleBlur);
		window.addEventListener('focus', handleFocus);

		return () => {
			clearRefreshTimeout();
			window.removeEventListener('blur', handleBlur);
			window.removeEventListener('focus', handleFocus);
		};
	}, [accessJwtPayload]);

	useEffect(() => {
		if (!tokens?.accessToken) {
			setAccessJwtPayload(null);
			return;
		}

		try {
			const accessTokenParsed = parseJwt(tokens.accessToken.token);
			const exp = accessTokenParsed.exp * 1000;
			const now = Date.now();
			if (exp < now) refreshTokens();
			else setAccessJwtPayload(accessTokenParsed);
		} catch (e) {
			console.error(`Clear token from local storage, could not be parsed: ${e}`);
			clearTokens();
		}
	}, [tokens]);

	const refreshTokens = async () => {
		if (!tokens?.refreshToken) {
			clearTokens();
			return;
		}

		const response = await apiRefreshToken.callApi(tokens.refreshToken);
		response.do(setTokens).orElseDo(refreshTokensAfterBackOnline);
	};

	const refreshTokensAfterBackOnline = () => {
		if (onlineHandlerRef.current) return;
		onlineHandlerRef.current = refreshTokens;
		window.addEventListener('online', onlineHandlerRef.current);
	};

	const login = async (email: string, password: string) => {
		const response = await apiLogin.callApi(email, password);
		response.do(setTokens);
	};

	const logout = async () => {
		if (!tokens?.refreshToken) {
			clearTokens();
			return;
		}
		await apiLogout.callApi(tokens.refreshToken).finally(clearTokens);
	};

	const setTokens = (newTokens: ITokens | null) => {
		if (!newTokens) {
			clearTokens();
			return;
		}

		setTokensState(newTokens);
		localStorage.setItem(TOKENS_LOCAL_STORAGE_KEY, JSON.stringify(newTokens));
		channelRef.current?.postMessage(newTokens);
	};

	const clearRefreshTimeout = () => {
		if (!refreshTimeoutRef.current) return;
		clearTimeout(refreshTimeoutRef.current);
		refreshTimeoutRef.current = null;
	};

	const clearBackOnlineHandler = () => {
		if (!onlineHandlerRef.current) return;
		window.removeEventListener('online', onlineHandlerRef.current);
		onlineHandlerRef.current = null;
	};

	const clearTokens = () => {
		clearRefreshTimeout();
		clearBackOnlineHandler();
		setTokensState(null);
		localStorage.removeItem(TOKENS_LOCAL_STORAGE_KEY);
		channelRef.current?.postMessage(null);
	};

	const arePermissionsInToken = (permissions: PERMISSIONS[]): boolean => {
		if (!permissions.length) return true;
		const tokenPermissions: PERMISSIONS[] = accessJwtPayload?.permission ?? [];
		return permissions.some((i) => tokenPermissions.includes(i));
	};

	const tokensStr = localStorage.getItem(TOKENS_LOCAL_STORAGE_KEY);
	if (accessJwtPayload == null && tokensStr != null) {
		// app has recently opened and it still refreshing the JWT
		return <LoadingAnimation isLoading />;
	}

	return (
		<JwtContext.Provider
			value={{ setTokens, accessJwt: accessJwtPayload, login, logout, arePermissionsInToken }}
		>
			{children}
		</JwtContext.Provider>
	);
};
